/**
 * Secure Logging Utility
 * 
 * Provides environment-aware logging that:
 * - Prevents sensitive data exposure in production
 * - Sanitizes error messages and data
 * - Supports structured error reporting
 * - Maintains debugging capabilities in development
 * 
 * ## Debug Modes
 * 
 * ### Development Mode (NODE_ENV === 'development')
 * - All logs show with full detail
 * - Rich debugging with console groups and full context
 * 
 * ### Production Mode (NODE_ENV === 'production') 
 * - Only error/warning logs show (info/debug are silent)
 * - Data is sanitized to remove PII/sensitive information
 * 
 * ## Enabling Debug Logging in Production
 * 
 * ### Method 1: Environment Variable (Deploy-time)
 * Set `REACT_APP_DEBUG_LOGGING=true` in your environment
 * 
 * ### Method 2: Browser Console (Runtime - for user support)
 * ```javascript
 * // Enable debug logging
 * nucleusLogger.enable()
 * 
 * // Check current status
 * nucleusLogger.status()  // Returns debug mode status
 * 
 * // Disable debug logging
 * nucleusLogger.disable()
 * ```
 * 
 * ### Method 3: localStorage (Direct)
 * ```javascript
 * // Enable
 * localStorage.setItem('nucleus_debug_logging', 'true')
 * 
 * // Disable  
 * localStorage.removeItem('nucleus_debug_logging')
 * 
 * // Then refresh the page
 * ```
 * 
 * ## User Support Workflow
 * 1. Ask user to open browser console (F12)
 * 2. Type: `nucleusLogger.enable()` 
 * 3. Ask user to refresh page and reproduce issue
 * 4. Ask user to check console for detailed logs
 * 5. Type: `nucleusLogger.disable()` when done
 */

// Environment detection with debug override capability
// Use REACT_APP_ENVIRONMENT (your existing setup)
const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';
const isDebugMode = isDevelopment || 
  process.env.REACT_APP_DEBUG_LOGGING === 'true' || 
  (typeof window !== 'undefined' && window.localStorage?.getItem('nucleus_debug_logging') === 'true');

// Error types for structured logging
export enum ErrorType {
  JSON_PARSE = 'JSON_PARSE_ERROR',
  API_ERROR = 'API_ERROR',
  AUTH_ERROR = 'AUTH_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  PAYMENT_ERROR = 'PAYMENT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// Data sanitization utilities
const sanitizeErrorData = (data: any): any => {
  if (typeof data === 'string') {
    // Remove potential PII patterns
    return data
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]')
      .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN]')
      .replace(/\b\d{16}\b/g, '[CARD]')
      .replace(/Bearer\s+[^\s]+/gi, 'Bearer [TOKEN]')
      .replace(/api[_-]?key[^a-zA-Z0-9]*[a-zA-Z0-9]{10,}/gi, 'apikey=[REDACTED]');
  }
  
  if (typeof data === 'object' && data !== null) {
    const sanitized: any = Array.isArray(data) ? [] : {};
    
    for (const [key, value] of Object.entries(data)) {
      // Skip sensitive fields entirely
      const sensitiveFields = ['password', 'token', 'secret', 'key', 'authorization', 'cookie'];
      if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
        sanitized[key] = '[REDACTED]';
        continue;
      }
      
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeErrorData(value);
    }
    
    return sanitized;
  }
  
  return data;
};

// Secure logging interface
export interface LogContext {
  errorType?: ErrorType;
  userId?: string;
  businessId?: string;
  component?: string;
  metadata?: Record<string, any>;
}

class SecureLogger {
  /**
   * Log error with environment-appropriate handling
   */
  error(message: string, error?: Error | any, context?: LogContext): void {
    if (isDebugMode) {
      // Full debugging in development or debug mode
      console.group(`🚨 ${context?.errorType || 'ERROR'}: ${message}`);
      if (error) console.error('Error:', error);
      if (context?.metadata) console.error('Context:', context.metadata);
      if (context?.component) console.error('Component:', context.component);
      console.groupEnd();
    } else {
      // Sanitized logging in production
      const sanitizedContext = context ? sanitizeErrorData(context) : undefined;
      const errorCode = context?.errorType || ErrorType.UNKNOWN_ERROR;
      
      // TODO: Send to error monitoring service (Sentry)
      // For now, log minimal sanitized information
      console.error(`[${errorCode}] ${message}`, sanitizedContext);
    }
  }

  /**
   * AUTH-SAFE ERROR LOGGING
   * Simplified logging for authentication flows that won't trigger re-renders
   * Uses setTimeout to defer logging and avoid synchronous side effects
   */
  authError(message: string, error?: Error | any, context?: Omit<LogContext, 'errorType'>): void {
    // Defer logging to avoid interfering with auth flows
    setTimeout(() => {
      if (isDebugMode) {
        console.group(`🔐 AUTH_ERROR: ${message}`);
        if (error) console.error('Error:', error);
        if (context?.component) console.error('Component:', context.component);
        console.groupEnd();
      } else {
        // Minimal production logging for auth errors
        console.error(`[AUTH_ERROR] ${message}`);
      }
    }, 0);
  }

  /**
   * Log warning with conditional output
   */
  warn(message: string, data?: any, context?: LogContext): void {
    if (isDebugMode) {
      console.warn(`⚠️ ${message}`, data);
    } else if (context?.errorType) {
      // Only log categorized warnings in production
      console.warn(`[${context.errorType}] ${message}`);
    }
  }

  /**
   * AUTH-SAFE WARNING LOGGING
   * Deferred warning logging for authentication operations
   */
  authWarn(message: string, data?: any): void {
    setTimeout(() => {
      if (isDebugMode) {
        console.warn(`🔐 AUTH_WARN: ${message}`, data);
      } else {
        console.warn(`[AUTH_WARN] ${message}`);
      }
    }, 0);
  }

  /**
   * Log info (development only)
   */
  info(message: string, data?: any): void {
    if (isDebugMode) {
      console.log(`ℹ️ ${message}`, data);
    }
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, data?: any): void {
    if (isDebugMode) {
      console.debug(`🔧 ${message}`, data);
    }
  }

  /**
   * Log JSON parsing errors with proper data handling
   */
  jsonParseError(jsonString: string, error: Error, context?: Omit<LogContext, 'errorType'>): void {
    const errorContext: LogContext = {
      ...context,
      errorType: ErrorType.JSON_PARSE,
      metadata: {
        stringLength: jsonString?.length || 0,
        errorMessage: error.message,
        // Only include string preview in development
        ...(isDebugMode && {
          firstChars: jsonString?.substring(0, 100),
          lastChars: jsonString?.substring(Math.max(0, (jsonString?.length || 0) - 100))
        })
      }
    };

    this.error('JSON parsing failed', error, errorContext);
  }

  /**
   * Helper methods for production debugging
   */
  enableDebugLogging(): void {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('nucleus_debug_logging', 'true');
      console.log('🔧 Debug logging ENABLED. Refresh the page to see debug logs.');
    }
  }

  disableDebugLogging(): void {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('nucleus_debug_logging');
      console.log('🔧 Debug logging DISABLED. Refresh the page to hide debug logs.');
    }
  }

  isDebugEnabled(): boolean {
    return isDebugMode;
  }

  getDebugStatus(): string {
    const environment = process.env.REACT_APP_ENVIRONMENT || 'unknown';
    
    if (isDevelopment) {
      return `🟢 Development mode (${environment}) - debug always enabled`;
    } else if (process.env.REACT_APP_DEBUG_LOGGING === 'true') {
      return `🟡 Production mode (${environment}) with ENV debug enabled`;
    } else if (typeof window !== 'undefined' && window.localStorage?.getItem('nucleus_debug_logging') === 'true') {
      return `🟡 Production mode (${environment}) with localStorage debug enabled`;
    } else {
      return `🔴 Production mode (${environment}) - debug disabled`;
    }
  }
}

// Export the logger instance
export const logger = new SecureLogger();

// Make logger available globally for debugging (both development and production)
if (typeof window !== 'undefined') {
  (window as any).nucleusLogger = {
    enable: () => logger.enableDebugLogging(),
    disable: () => logger.disableDebugLogging(),
    status: () => logger.getDebugStatus(),
    isEnabled: () => logger.isDebugEnabled()
  };
}

// Legacy console replacement helpers
export const secureConsole = {
  error: (message: string, ...args: any[]) => {
    logger.error(message, args.length === 1 ? args[0] : args);
  },
  warn: (message: string, ...args: any[]) => {
    logger.warn(message, args.length === 1 ? args[0] : args);
  },
  log: (message: string, ...args: any[]) => {
    logger.info(message, args.length === 1 ? args[0] : args);
  },
  debug: (message: string, ...args: any[]) => {
    logger.debug(message, args.length === 1 ? args[0] : args);
  }
}; 